<script setup lang="ts">
import { Card, NoteComponent } from '@metapic-apps/shared'
import type { FormInstance, FormRules } from 'element-plus'
import { computed, reactive, ref, watch } from 'vue'

import DeleteIcon from '@/assets/icons/delete.svg?component'
import CampaignFormCalendar from '@/components/CampaignForm/CampaignFormCalendar.vue'
import CampaignFormCampaignDetails from '@/components/CampaignForm/CampaignFormCampaignDetails.vue'
import CampaignFormContent from '@/components/CampaignForm/CampaignFormContent.vue'
import CampaignFormNavigation from '@/components/CampaignForm/CampaignFormNavigation.vue'
import CampaignFormPayment from '@/components/CampaignForm/CampaignFormPayment.vue'
import CampaignFormPriority from '@/components/CampaignForm/CampaignFormPriority/CampaignFormPriority.vue'
import CampaignFormTargetBlock from '@/components/CampaignForm/CampaignFormTargetBlock/CampaignFormTargetBlock.vue'
import { MAX_MONITORED_CREATORS } from '@/lib/constants/campaign.constants'
import { useBackToPreviousPage } from '@/lib/hooks/back-to-previous-page.hooks'
import { maxAmountRule, positiveAmountRule } from '@/lib/rules/common.rules'
import { type CampaignFormType, CampaignTypeEnum } from '@/lib/types/campaign.types'
import { TargetItemEnum } from '@/lib/types/common.types'
import { BoxComponent, ButtonComponent, FlexComponent, TitleComponent, TypographyComponent } from '@/ui-library'

const handleBack = useBackToPreviousPage({ name: 'campaigns' })

const emit = defineEmits(['handleSubmit', 'handleDelete'])

const props = withDefaults(
  defineProps<{
    defaultAdvertiserGroupId: number
    isLoading?: boolean
    error?: Error | null
    campaign?: CampaignFormType
    isEditMode?: boolean
    endReason?: string
  }>(),
  {
    isLoading: false,
    error: null,
    campaign: undefined,
    isEditMode: false,
  },
)

const isDirty = ref(false)
const ruleFormRef = ref<FormInstance>()
const formData = reactive<Omit<CampaignFormType, 'status'>>(
  props.campaign || {
    advertiserGroup: props.defaultAdvertiserGroupId,
    advertiserList: [],
    hasTargetAllCreators: false,
    hasPreregisterCreators: false,
    targets: [],
    hasUnmatchedTargets: false,
    preregisterItems: [],
    campaignType: CampaignTypeEnum.Standard,
    internalName: '',
    title: '',
    description: '',
    hasBadge: false,
    hasProductSeeding: false,
    isShownForAdvertiser: false,
    hasTodoItems: false,
    todoList: [{ key: 0, value: '' }],
    hashtags: [],
    tempCampaignImage: undefined,
    invoiceAdvertiserDirectly: false,
    trafficSourceCosts: [],
    hasAdditionalPayment: false,
    creatorCost: 0,
    hasInvoiceAdvertiserDirectly: false,
    revenueInvoice: 0,
    hasCommission: true,
    hasStartNow: true,
    hasOpenEnd: true,
    validFrom: '',
    validUntil: '',
    perUserLimit: false,
    priority: {},
    hasBudget: false,
    currency: '',
  },
)

const isContentDisabled = computed(() => formData.campaignType === CampaignTypeEnum.Standard)

const showMonitoringLimitNote = computed(
  () =>
    formData.hashtags.length > 0 &&
    (formData.hasTargetAllCreators ||
      formData.targets.length > MAX_MONITORED_CREATORS ||
      formData.targets.some((target) => target.type !== TargetItemEnum.User)),
)

const getDecimalPlaces = (value: string | number): number => {
  const [, decimals] = String(value).split('.')
  return decimals?.length ?? 0
}

const rules = reactive<FormRules>({
  advertiserList: [{ required: true, message: 'This field is required', trigger: 'change' }],
  todoList: [{ required: true, message: 'This field is required', trigger: 'blur' }],
  internalName: [
    { max: 255, message: 'Field must be shorter than 255 symbols', trigger: 'blur' },
    {
      validator: (_, value: string, callback) => {
        if (formData.campaignType === CampaignTypeEnum.Standard && !value) {
          callback(new Error('This field is required'))
        } else {
          callback()
        }
      },
      trigger: 'blur',
    },
  ],
  title: [
    { required: true, message: 'This field is required', trigger: 'blur' },
    {
      validator: (_, value: string, callback) => {
        if (formData.campaignType !== CampaignTypeEnum.Standard && !value) {
          callback(new Error('This field is required'))
        } else {
          callback()
        }
      },
      trigger: 'blur',
    },
    { max: 255, message: 'Field must be shorter than 255 symbols', trigger: 'blur' },
  ],
  description: [
    {
      validator: (_, value: string, callback) => {
        if (formData.campaignType !== CampaignTypeEnum.Standard && !value) {
          callback(new Error('This field is required'))
        } else {
          callback()
        }
      },
      trigger: 'blur',
    },
    { max: 1056, message: 'Field must be shorter than 1056 symbols', trigger: 'blur' },
  ],
  targets: [
    {
      validator: (_, value: string, callback) => {
        if (!formData.hasTargetAllCreators && formData.hasUnmatchedTargets) {
          callback(new Error('Delete unmatched creators'))
        } else {
          callback()
        }
      },
      trigger: 'submit',
    },
  ],
  creatorCost: [
    {
      validator: (_, value: string | number, callback) => {
        const amount = Number(value)
        if (!value || isNaN(amount) || amount <= 0) {
          callback(new Error('Value must be greater than 0'))
        } else if (getDecimalPlaces(value) > 2) {
          callback(new Error('Field must have at most 2 decimal places'))
        } else {
          callback()
        }
      },
      trigger: 'blur',
    },
  ],
  revenueInvoice: [
    {
      validator: (_, value: string | number, callback) => {
        if (!formData.hasAdditionalPayment || !formData.hasInvoiceAdvertiserDirectly) {
          callback()
        } else if (!value || isNaN(Number(value)) || Number(value) <= 0) {
          callback(new Error('Value must be greater than 0'))
        } else if (getDecimalPlaces(value) > 2) {
          callback(new Error('Field must have at most 2 decimal places'))
        } else {
          callback()
        }
      },
      trigger: 'blur',
    },
  ],
  maxClicks: [positiveAmountRule, maxAmountRule],
  hasBudget: [
    {
      validator: (_, value: boolean, callback) => {
        if (!formData.sharedBudget && !formData.budgetLimit && value) {
          callback(new Error('Please select a shared budget, or budget limit, or turn this switch off'))
        } else {
          callback()
        }
      },
      trigger: 'blur',
    },
  ],
  budgetLimit: [
    positiveAmountRule,
    maxAmountRule,
    {
      validator: (_, value: number, callback: (e?: Error) => void) => {
        if (!value) {
          return callback()
        }

        if (formData.sharedBudget && value > formData.sharedBudget?.amount?.amount) {
          return callback(new Error('Campaign limit must be less than shared budget'))
        }

        return callback()
      },
      trigger: 'blur',
    },
  ],
  validFrom: [{ type: 'date', message: 'Please pick a valid date', trigger: 'blur' }],
  validUntil: [
    { type: 'date', message: 'Please pick a valid date', trigger: 'blur' },
    {
      validator: (_, value: Date, callback) => {
        if (formData.validFrom && new Date(value).getTime() <= new Date(formData.validFrom).getTime()) {
          callback(new Error('End date should be greater then start date'))
        } else {
          callback()
        }
      },
      trigger: 'blur',
    },
    {
      validator: (_, value: Date, callback) => {
        if (formData.hashtags.length && !value) {
          callback(new Error('An end date is required when hashtags are set'))
        } else {
          callback()
        }
      },
      trigger: 'submit',
    },
  ],
})

const submitForm = (formEl: FormInstance | undefined) => {
  if (!formEl) {
    return
  }

  return formEl.validate((valid) => {
    if (valid) {
      isDirty.value = false
      emit('handleSubmit', formData)
    }
  })
}

watch(
  () => formData.hasUnmatchedTargets,
  async () => {
    if (!formData.hasUnmatchedTargets) {
      await ruleFormRef.value?.validateField('targets')
    }
  },
  { deep: true },
)

watch(
  () => formData.hashtags.length,
  async (length) => {
    if (length && formData.validUntil) {
      await ruleFormRef.value?.validateField('validUntil')
    }
  },
)

watch(
  () => formData.campaignType,
  async (campaignType) => {
    if (campaignType === CampaignTypeEnum.Standard && formData.hashtags.length) {
      formData.hashtags = []
      await ruleFormRef.value?.validateField('validUntil')
    }
  },
  { immediate: true },
)
</script>
<template>
  <BoxComponent :mt="40" :mb="40" :ml="20" :mr="20">
    <BoxComponent v-if="endReason" :mb="48" :max-w="904" ml="auto" mr="auto">
      <NoteComponent> {{ endReason }} </NoteComponent>
    </BoxComponent>
    <FlexComponent class="campaign-form__wrapper" :gap="64" justify="center">
      <CampaignFormNavigation />
      <BoxComponent class="campaign-form__container">
        <el-form
          ref="ruleFormRef"
          :model="formData"
          :rules="rules"
          hide-required-asterisk
          :validate-on-rule-change="false"
          :scroll-to-error="true"
          label-position="top"
          class="campaign-form__content"
          @change="isDirty = true"
        >
          <FlexComponent direction="column" :gap="48">
            <Card id="campaign-form-block-target" title="Target">
              <CampaignFormTargetBlock
                v-model:advertiser-group="formData.advertiserGroup"
                v-model:advertiser-list="formData.advertiserList"
                v-model:has-target-all-creators="formData.hasTargetAllCreators"
                v-model:has-preregister-creators="formData.hasPreregisterCreators"
                v-model:targets="formData.targets"
                v-model:preregister-items="formData.preregisterItems"
                v-model:currency="formData.currency"
                v-model:has-unmatched-targets="formData.hasUnmatchedTargets"
                :is-edit-mode="isEditMode"
              />
            </Card>
            <Card id="campaign-form-block-campaign" title="Campaign">
              <CampaignFormCampaignDetails
                v-model:campaign-type="formData.campaignType"
                v-model:internal-name="formData.internalName"
                v-model:title="formData.title"
                v-model:description="formData.description"
                v-model:has-product-seeding="formData.hasProductSeeding"
                v-model:is-shown-for-advertiser="formData.isShownForAdvertiser"
                v-model:todo-items="formData.todoList"
                v-model:temp-campaign-image="formData.tempCampaignImage"
                v-model:has-badge="formData.hasBadge"
                v-model:has-todo-items="formData.hasTodoItems"
                :image="formData.image"
              />
            </Card>
            <Card id="campaign-form-block-content" title="Content">
              <CampaignFormContent
                v-model:hashtags="formData.hashtags"
                :disabled="isContentDisabled"
                :show-monitoring-limit-note="showMonitoringLimitNote"
              />
            </Card>
            <Card id="campaign-form-block-payment" title="Payment">
              <CampaignFormPayment
                v-model:traffic-source-costs="formData.trafficSourceCosts"
                v-model:has-commission="formData.hasCommission"
                v-model:invoice-advertiser-directly="formData.invoiceAdvertiserDirectly"
                v-model:has-additional-payment="formData.hasAdditionalPayment"
                v-model:creator-cost="formData.creatorCost"
                v-model:has-invoice-advertiser-directly="formData.hasInvoiceAdvertiserDirectly"
                v-model:revenue-invoice="formData.revenueInvoice"
                :currency="formData.currency"
              />
            </Card>
            <Card id="campaign-form-block-calendar" title="Start/End">
              <CampaignFormCalendar
                v-model:valid-from="formData.validFrom"
                v-model:valid-until="formData.validUntil"
                v-model:per-user-limit="formData.perUserLimit"
                v-model:has-start-now="formData.hasStartNow"
                v-model:has-open-end="formData.hasOpenEnd"
                v-model:max-clicks="formData.maxClicks"
                v-model:budget-limit="formData.budgetLimit"
                v-model:has-budget="formData.hasBudget"
                v-model:shared-budget="formData.sharedBudget"
                :advertiser-list="formData.advertiserList"
                :currency="formData.currency"
                :has-hashtags="formData.hashtags.length > 0"
              />
            </Card>
            <Card id="campaign-form-block-priority">
              <template #title>
                <TitleComponent title="Priority" variant="headline-medium">
                  <template #tooltip>
                    <BoxComponent :w="280">
                      <BoxComponent :mb="4">
                        <TypographyComponent variant="title-medium">Priority</TypographyComponent>
                      </BoxComponent>
                      <TypographyComponent variant="body-medium">
                        Priority indicates the campaign's importance. Drag and drop the campaign in your desired order. A higher order is
                        higher importance.
                      </TypographyComponent>
                    </BoxComponent>
                  </template>
                </TitleComponent>
              </template>
              <CampaignFormPriority
                v-model:priority="formData.priority"
                :is-edit-mode="isEditMode"
                :campaign-id="formData?.extra?.id"
                :advertiser-list="formData.advertiserList"
                :current-campaign-name="formData.internalName"
                :traffic-source-costs="formData.trafficSourceCosts"
                :currency="formData.currency"
              />
            </Card>
            <BoxComponent :mb="48">
              <FlexComponent justify="space-between">
                <ButtonComponent v-if="isEditMode" variant="text" :disabled="isLoading" data-test="deleteBtn" @click="emit('handleDelete')">
                  <template #iconLeft>
                    <el-icon :size="24">
                      <DeleteIcon class="delete-icon" />
                    </el-icon>
                  </template>
                  <TypographyComponent variant="body-medium" :font-weight="500" color="var(--error)"> Delete </TypographyComponent>
                </ButtonComponent>
                <FlexComponent :gap="8" justify="end" flex="1">
                  <ButtonComponent variant="secondary" :disabled="isLoading" @click="handleBack"> Cancel </ButtonComponent>
                  <ButtonComponent :is-loading="isLoading" data-test="submitFormBtn" @click.prevent="() => submitForm(ruleFormRef)">
                    {{ isEditMode ? 'Save' : 'Start' }} campaign
                  </ButtonComponent>
                </FlexComponent>
              </FlexComponent>
            </BoxComponent>
          </FlexComponent>
        </el-form>
      </BoxComponent>
    </FlexComponent>
  </BoxComponent>
</template>
<style lang="scss" scoped>
.campaign-form__wrapper {
  position: relative;
}
.campaign-form__container {
  width: 640px;
}

:deep(.delete-icon) {
  path {
    fill: var(--error);
  }
}
</style>
